import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowRight, Shield, Zap, Users, BarChart3, Mail, Phone, Lock, User, Building, KeyRound, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LoginPage() {
  const navigate = useNavigate();
  const { login, checkAuth, setUser } = useAuthStore();
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'google', 'otp-verify'
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationError, setVerificationError] = useState('');
  
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  
  const [signupForm, setSignupForm] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    workspace_name: ''
  });

  React.useEffect(() => {
    // Check if already authenticated
    checkAuth().then((user) => {
      if (user) {
        navigate('/dashboard');
      }
    });
  }, [checkAuth, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!loginForm.email || !loginForm.password) {
      toast.error('Email and password are required');
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginForm)
      });
      
      if (response.ok) {
        const user = await response.json();
        setUser(user);
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!signupForm.name || !signupForm.email || !signupForm.mobile || !signupForm.password) {
      toast.error('All fields are required');
      return;
    }
    
    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(signupForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Mobile validation
    const mobileRegex = /^\+?[0-9]{10,15}$/;
    if (!mobileRegex.test(signupForm.mobile.replace(/[\s-]/g, ''))) {
      toast.error('Please enter a valid mobile number (10-15 digits)');
      return;
    }
    
    // Password validation
    if (signupForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (signupForm.password !== signupForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      
      // Step 1: Request OTP
      const otpResponse = await fetch(`${API_URL}/api/auth/signup/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: signupForm.email,
          name: signupForm.name
        })
      });
      
      if (otpResponse.ok) {
        setOtpSent(true);
        setMode('otp-verify');
        toast.success('Verification code sent to your email!');
        // Start cooldown for resend
        setResendCooldown(60);
        const timer = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        const error = await otpResponse.json();
        toast.error(error.detail || 'Failed to send verification code');
      }
    } catch (error) {
      toast.error('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setVerificationError('');
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/signup/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: signupForm.email,
          otp: otp,
          name: signupForm.name,
          mobile: signupForm.mobile,
          password: signupForm.password,
          workspace_name: signupForm.workspace_name || undefined
        })
      });
      
      if (response.ok) {
        const user = await response.json();
        setUser(user);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Verification failed';
        console.error('Verification error:', errorMessage);
        setVerificationError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Verification exception:', error);
      const errorMsg = 'Network error. Please check your connection and try again.';
      setVerificationError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/signup/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: signupForm.email,
          name: signupForm.name
        })
      });
      
      if (response.ok) {
        toast.success('New verification code sent!');
        setOtp('');
        setResendCooldown(60);
        const timer = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to resend code');
      }
    } catch (error) {
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div 
        className="hidden lg:flex lg:w-2/5 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1753234506830-069cff1f25ae?crop=entropy&cs=srgb&fm=jpg&q=85)' 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary/40 backdrop-blur-sm" />
        <div className="relative z-10 flex flex-col justify-between p-10 text-white">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-sm bg-white/20 backdrop-blur flex items-center justify-center">
                <span className="font-heading font-bold text-2xl">YG</span>
              </div>
              <span className="font-heading font-bold text-2xl">YellowGold Pro</span>
            </div>
          </div>
          
          <div className="space-y-6">
            <h1 className="font-heading text-4xl font-bold leading-tight">
              Professional Match & Session Settlement Platform
            </h1>
            <p className="text-white/80 text-lg">
              Desktop-grade accounting for the modern web. Real-time updates, 
              multi-tenant isolation, and deterministic calculations.
            </p>
            
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-white/80" />
                <span className="text-sm">Multi-tenant Security</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-white/80" />
                <span className="text-sm">Real-time Updates</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-white/80" />
                <span className="text-sm">Multi-user Support</span>
              </div>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-white/80" />
                <span className="text-sm">Detailed Reports</span>
              </div>
            </div>
          </div>
          
          <div className="text-sm text-white/60">
            © 2025 YellowGold Pro. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Panel - Login/Signup Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center">
              <span className="font-heading font-bold text-xl text-primary-foreground">YG</span>
            </div>
            <span className="font-heading font-bold text-xl">YellowGold Pro</span>
          </div>

          <div className="text-center">
            <h2 className="font-heading text-3xl font-bold">
              {mode === 'otp-verify' ? 'Verify Your Email' : mode === 'signup' ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p className="text-muted-foreground mt-2">
              {mode === 'otp-verify'
                ? `Enter the verification code sent to ${signupForm.email}`
                : mode === 'signup' 
                  ? 'Sign up to get started with your workspace' 
                  : 'Sign in to access your workspace'}
            </p>
          </div>

          {/* Mode Toggle - hide during OTP verification */}
          {mode !== 'otp-verify' && (
            <div className="flex bg-muted rounded-lg p-1">
              <button
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  mode !== 'signup' ? 'bg-background shadow' : ''
                }`}
                onClick={() => setMode('login')}
                data-testid="login-tab"
              >
                Sign In
              </button>
              <button
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  mode === 'signup' ? 'bg-background shadow' : ''
                }`}
                onClick={() => setMode('signup')}
                data-testid="signup-tab"
              >
                Sign Up
              </button>
            </div>
          )}

          {mode === 'otp-verify' ? (
            /* OTP Verification Form */
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <KeyRound className="w-8 h-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  We've sent a 6-digit verification code to<br />
                  <span className="font-semibold text-foreground">{signupForm.email}</span>
                </p>
              </div>
              
              <div>
                <Label className="flex items-center gap-1 justify-center mb-2">
                  <KeyRound className="h-4 w-4" />
                  Verification Code
                </Label>
                <Input 
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setVerificationError(''); // Clear error when user types
                  }}
                  placeholder="Enter 6-digit code"
                  className="text-center text-2xl tracking-widest font-mono h-14"
                  maxLength={6}
                  autoFocus
                  data-testid="otp-input"
                />
              </div>
              
              {/* Error Display */}
              {verificationError && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="verification-error">
                  {verificationError}
                </div>
              )}
              
              <Button 
                type="submit"
                className="w-full h-12"
                disabled={loading || otp.length !== 6}
                data-testid="verify-otp-btn"
              >
                {loading ? 'Verifying...' : 'Verify & Create Account'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Didn't receive the code?
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResendOTP}
                  disabled={resendCooldown > 0 || loading}
                  className="text-primary"
                  data-testid="resend-otp-btn"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </Button>
              </div>
              
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setMode('signup');
                  setOtpSent(false);
                  setOtp('');
                  setVerificationError('');
                }}
                data-testid="back-to-signup-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sign Up
              </Button>
            </form>
          ) : mode === 'signup' ? (
            /* Signup Form */
            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <Label className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  Full Name *
                </Label>
                <Input 
                  value={signupForm.name}
                  onChange={(e) => setSignupForm({...signupForm, name: e.target.value})}
                  placeholder="Enter your full name"
                  data-testid="signup-name-input"
                />
              </div>
              
              <div>
                <Label className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  Email *
                </Label>
                <Input 
                  type="email"
                  value={signupForm.email}
                  onChange={(e) => setSignupForm({...signupForm, email: e.target.value})}
                  placeholder="Enter your email"
                  data-testid="signup-email-input"
                />
              </div>
              
              <div>
                <Label className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  Mobile Number *
                </Label>
                <Input 
                  type="tel"
                  value={signupForm.mobile}
                  onChange={(e) => setSignupForm({...signupForm, mobile: e.target.value})}
                  placeholder="+91 9876543210"
                  data-testid="signup-mobile-input"
                />
              </div>
              
              <div>
                <Label className="flex items-center gap-1">
                  <Lock className="h-4 w-4" />
                  Password *
                </Label>
                <Input 
                  type="password"
                  value={signupForm.password}
                  onChange={(e) => setSignupForm({...signupForm, password: e.target.value})}
                  placeholder="Min. 6 characters"
                  data-testid="signup-password-input"
                />
              </div>
              
              <div>
                <Label className="flex items-center gap-1">
                  <Lock className="h-4 w-4" />
                  Confirm Password *
                </Label>
                <Input 
                  type="password"
                  value={signupForm.confirmPassword}
                  onChange={(e) => setSignupForm({...signupForm, confirmPassword: e.target.value})}
                  placeholder="Re-enter password"
                  data-testid="signup-confirm-password-input"
                />
              </div>
              
              <div>
                <Label className="flex items-center gap-1">
                  <Building className="h-4 w-4" />
                  Workspace Name (Optional)
                </Label>
                <Input 
                  value={signupForm.workspace_name}
                  onChange={(e) => setSignupForm({...signupForm, workspace_name: e.target.value})}
                  placeholder="My Company"
                  data-testid="signup-workspace-input"
                />
              </div>
              
              <Button 
                type="submit"
                className="w-full h-12"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? 'Sending Code...' : 'Send Verification Code'}
                <Mail className="w-4 h-4 ml-2" />
              </Button>
            </form>
          ) : (
            /* Login Form */
            <div className="space-y-4">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    Email or Mobile
                  </Label>
                  <Input 
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                    placeholder="Enter email or mobile"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-1">
                    <Lock className="h-4 w-4" />
                    Password
                  </Label>
                  <Input 
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                    placeholder="Enter password"
                    data-testid="login-password-input"
                  />
                </div>
                
                <Button 
                  type="submit"
                  className="w-full h-12"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>
              
              <Button 
                onClick={login}
                variant="outline"
                className="w-full h-12"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Sign in to YellowGoldPro
              </Button>
            </div>
          )}

          <div className="text-center text-sm text-muted-foreground">
            <p>
              By signing in, you agree to our{' '}
              <a href="#" className="text-primary hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-primary hover:underline">Privacy Policy</a>
            </p>
          </div>

          <div className="pt-4 border-t">
            <div className="text-center text-sm text-muted-foreground">
              <p className="font-medium">Features included:</p>
              <ul className="mt-2 space-y-1">
                <li>✓ Client & Match Management</li>
                <li>✓ Real-time Transaction Updates</li>
                <li>✓ Settlement & Reports</li>
                <li>✓ MCX/EQ/F&O Meta Transactions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
